import {
  ActionIcon,
  Avatar,
  Code,
  CopyButton,
  DataList,
  Group,
  NumberFormatter,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCopy, IconCopyCheck } from '@tabler/icons-react';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { isNonNullish } from 'remeda';
import {
  getCurrencySymbolFromCurrencyCode,
  useLocalizedCurrencyFormatter,
} from 'shared/utils/currency';

import classes from './ClientHoverCardMetrics.module.css';
import { useClientHoverCardData } from './useClientHoverCardData';

type ClientHoverCardMetricsProps = {
  debtorId: string;
};

export const ClientHoverCardMetrics = ({ debtorId }: ClientHoverCardMetricsProps) => {
  const { t } = useTranslation();
  const { debtor, isDebtorReady, hasDebtorError } = useClientHoverCardData(debtorId);

  const accountManager = debtor?.account_manager;
  const isDebtorPending = !isDebtorReady && !hasDebtorError;
  const shouldShowVatNumber = isDebtorPending || Boolean(debtor?.vat_number);
  const shouldShowExternalId = isDebtorPending || isNonNullish(debtor?.external_id);
  const shouldShowAccountManager = isDebtorPending || isNonNullish(accountManager);

  return (
    <DataList
      gap={0}
      labelWidth={160}
      classNames={{ item: classes.item, itemValue: classes.itemValue }}
    >
      <DataList.Item>
        <DataList.ItemLabel>
          {t(i18nKeys.CLIENT.GENERAL_INFO_TAB.PANEL_TITLE.BALANCE)}
        </DataList.ItemLabel>
        <DataList.ItemValue>
          <BalanceMetric debtorId={debtorId} />
        </DataList.ItemValue>
      </DataList.Item>

      <DataList.Item>
        <DataList.ItemLabel>
          {t(i18nKeys.CLIENT.GENERAL_INFO_TAB.ANNUAL_TURNOVER_SHARE)}
        </DataList.ItemLabel>
        <DataList.ItemValue>
          <AnnualTurnoverShareMetric debtorId={debtorId} />
        </DataList.ItemValue>
      </DataList.Item>

      <DataList.Item>
        <DataList.ItemLabel>
          {t(i18nKeys.CLIENT.GENERAL_INFO_TAB.PANEL_TITLE.AVERAGE_PAYMENT_DELAY)}
        </DataList.ItemLabel>
        <DataList.ItemValue>
          <AveragePaymentDelayMetric debtorId={debtorId} />
        </DataList.ItemValue>
      </DataList.Item>

      <DataList.Item>
        <DataList.ItemLabel>
          {t(i18nKeys.CLIENT.GENERAL_INFO_TAB.PANEL_TITLE.LATE_PAYMENT_RATE)}
        </DataList.ItemLabel>
        <DataList.ItemValue>
          <LatePaymentRateMetric debtorId={debtorId} />
        </DataList.ItemValue>
      </DataList.Item>

      {shouldShowVatNumber && (
        <DataList.Item>
          <DataList.ItemLabel>{t(i18nKeys.VAT)}</DataList.ItemLabel>
          <DataList.ItemValue>
            {debtor?.vat_number ? (
              <Group gap={4} wrap="nowrap">
                <Code w="fit-content">{debtor.vat_number}</Code>
                <CopyValueButton value={debtor.vat_number} />
              </Group>
            ) : (
              <MetricSkeleton />
            )}
          </DataList.ItemValue>
        </DataList.Item>
      )}

      {shouldShowExternalId && (
        <DataList.Item>
          <DataList.ItemLabel>{t(i18nKeys.CLIENT.EXTERNAL_ID)}</DataList.ItemLabel>
          <DataList.ItemValue>
            {isNonNullish(debtor?.external_id) ? (
              <Group gap={4} wrap="nowrap">
                <Code w="fit-content">{debtor.external_id}</Code>
                <CopyValueButton value={String(debtor.external_id)} />
              </Group>
            ) : (
              <MetricSkeleton />
            )}
          </DataList.ItemValue>
        </DataList.Item>
      )}

      {shouldShowAccountManager && (
        <DataList.Item>
          <DataList.ItemLabel>{t(i18nKeys.ACCOUNT_MANAGER)}</DataList.ItemLabel>
          <DataList.ItemValue>
            {isNonNullish(accountManager) ? (
              <Group gap="xs" wrap="nowrap">
                <Avatar
                  name={`${accountManager.first_name} ${accountManager.last_name}`}
                  size="sm"
                  radius="xl"
                  color="blue"
                />
                <Text size="sm" c="gray.10" truncate>
                  {accountManager.first_name} {accountManager.last_name}
                </Text>
              </Group>
            ) : (
              <Group gap="xs" wrap="nowrap">
                <Skeleton h={26} w={26} radius="xl" />
                <MetricSkeleton />
              </Group>
            )}
          </DataList.ItemValue>
        </DataList.Item>
      )}
    </DataList>
  );
};

const MetricUnavailable = () => {
  const { t } = useTranslation();

  return (
    <Text size="xs" c="dimmed">
      {t(i18nKeys.CLIENT.GENERAL_INFO_TAB.COULD_NOT_FETCH)}
    </Text>
  );
};

const MetricSkeleton = () => <Skeleton h={14} w={120} />;

const CopyValueButton = ({ value }: { value: string }) => {
  const { t } = useTranslation();

  return (
    <CopyButton value={value} timeout={2000}>
      {({ copied: isCopied, copy }) => (
        <Tooltip label={isCopied ? t(i18nKeys.COPIED) : t(i18nKeys.COPY)}>
          <ActionIcon
            size={24}
            variant="light"
            color={isCopied ? 'validation' : 'gray'}
            radius="sm"
            aria-label={isCopied ? t(i18nKeys.COPIED) : t(i18nKeys.COPY)}
            onClick={copy}
          >
            {isCopied ? <IconCopyCheck size={14} stroke={2} /> : <IconCopy size={14} stroke={2} />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
};

type MetricProps = {
  debtorId: string;
};

const BalanceMetric = ({ debtorId }: MetricProps) => {
  const { t } = useTranslation();
  const { balance, isDebtorBalanceInitialLoading, hasBalanceError } =
    useClientHoverCardData(debtorId);
  const formatter = useLocalizedCurrencyFormatter();

  if (hasBalanceError) return <MetricUnavailable />;
  if (isDebtorBalanceInitialLoading || balance === null) return <MetricSkeleton />;

  return (
    <Stack gap={0}>
      <Text size="sm" fw={600} c={balance.remainingBalance === 0 ? 'validation.7' : 'error.5'}>
        {formatter.format(balance.remainingBalance)}
      </Text>
      <Text size="xs" c="dimmed">
        {`${balance.openInvoices.length} ${t(i18nKeys.CLIENT.GENERAL_INFO_TAB.OPEN_INVOICES)}`}
      </Text>
    </Stack>
  );
};

const AnnualTurnoverShareMetric = ({ debtorId }: MetricProps) => {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const {
    debtor,
    balance,
    isDebtorReady,
    isDebtorBalanceInitialLoading,
    hasDebtorError,
    hasBalanceError,
  } = useClientHoverCardData(debtorId);

  if (hasBalanceError || hasDebtorError) return <MetricUnavailable />;
  if (!isDebtorReady || isDebtorBalanceInitialLoading || debtor === null || balance === null)
    return <MetricSkeleton />;

  return (
    <Stack gap={0}>
      <Text size="sm" fw={600}>
        <NumberFormatter
          decimalScale={2}
          value={(debtor.annual_turnover_share ?? 0) * 100}
          suffix="%"
        />
      </Text>
      <Text size="xs" c="dimmed">
        <NumberFormatter
          value={balance.annualTurnover}
          suffix={`${getCurrencySymbolFromCurrencyCode(company.currency)} ${t(i18nKeys.CLIENT.GENERAL_INFO_TAB.BILLED_AMOUNT_SUFFIX)}`}
          decimalScale={2}
          thousandSeparator=" "
        />
      </Text>
    </Stack>
  );
};

const AveragePaymentDelayMetric = ({ debtorId }: MetricProps) => {
  const { t } = useTranslation();
  const { debtor, isDebtorReady, hasDebtorError } = useClientHoverCardData(debtorId);

  if (hasDebtorError) return <MetricUnavailable />;
  if (!isDebtorReady || debtor === null) return <MetricSkeleton />;

  return (
    <Text size="sm" fw={600}>
      <NumberFormatter
        value={Math.round(debtor.average_payment_delay)}
        suffix={` ${t(i18nKeys.DAY)}`}
      />
    </Text>
  );
};

const LatePaymentRateMetric = ({ debtorId }: MetricProps) => {
  const { debtor, isDebtorReady, hasDebtorError } = useClientHoverCardData(debtorId);

  if (hasDebtorError) return <MetricUnavailable />;
  if (!isDebtorReady || debtor === null) return <MetricSkeleton />;

  return (
    <Text size="sm" fw={600}>
      <NumberFormatter decimalScale={2} value={debtor.delay_rate} suffix="%" />
    </Text>
  );
};
